import { useSyncExternalStore } from 'react';

export const BP = { md: 768, lg: 1024, xl: 1280 } as const;

type MediaQuerySubscriber = (cb: () => void) => () => void;
const subscriberCache = new Map<string, MediaQuerySubscriber>();

function getSubscriber(query: string): MediaQuerySubscriber {
  let sub = subscriberCache.get(query);
  if (!sub) {
    sub = (cb: () => void) => {
      if (typeof window === 'undefined') {
        return () => {};
      }
      const cleanups: Array<() => void> = [];
      if (typeof window.matchMedia === 'function') {
        const mql = window.matchMedia(query);
        mql?.addEventListener?.('change', cb);
        cleanups.push(() => mql?.removeEventListener?.('change', cb));
      }
      window.addEventListener('resize', cb);
      cleanups.push(() => window.removeEventListener('resize', cb));

      return () => {
        cleanups.forEach((fn) => fn());
      };
    };
    subscriberCache.set(query, sub);
  }
  return sub;
}

export function matchesQuery(query: string): boolean {
  if (typeof window === 'undefined') return false;
  const minMatch = query.match(/min-width:\s*(\d+)px/);
  const maxMatch = query.match(/max-width:\s*(\d+)px/);

  if (minMatch && maxMatch) {
    return window.innerWidth >= Number(minMatch[1]) && window.innerWidth <= Number(maxMatch[1]);
  }
  if (minMatch) {
    return window.innerWidth >= Number(minMatch[1]);
  }
  if (maxMatch) {
    return window.innerWidth <= Number(maxMatch[1]);
  }

  if (typeof window.matchMedia === 'function') {
    return window.matchMedia(query)?.matches ?? false;
  }
  return false;
}

export function useMedia(query: string): boolean {
  return useSyncExternalStore(
    getSubscriber(query),
    () => matchesQuery(query),
    () => false
  );
}

export const useIsMobile = (): boolean => useMedia(`(max-width: ${BP.md - 1}px)`);
export const useIsTablet = (): boolean =>
  useMedia(`(min-width: ${BP.md}px) and (max-width: ${BP.xl - 1}px)`);
export const useIsWideDesktop = (): boolean => useMedia(`(min-width: ${BP.xl}px)`);
export const useIsTouch = (): boolean => useMedia('(pointer: coarse)');

export function useBreakpoint(): { isDesktop: boolean; isMobile: boolean } {
  const isMobile = useIsMobile();
  return { isDesktop: !isMobile, isMobile };
}
